import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzFilterOptionType, NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { TrackingLaneWheelComponent } from './tracking-lane-wheel/tracking-lane-wheel.component';
import { TrackingLaneLineComponent } from './tracking-lane-line/tracking-lane-line.component';
import { TrackingLaneConfigComponent } from './tracking-lane-config/tracking-lane-config.component';
import { ResolvedTrackingLaneNode, TrackingLane, TrackingLaneNode } from '../../model/iTrackingLane';
import { resolveTrackingLaneNodes } from './tracking-lane.resolver';
import { TrackingLaneService } from '../../services/tracking-lane/tracking-lane.service';
import { VariablesService } from '../../services/variables/variables.service';
import { IColumnDefinition } from '../standard-tile/standard-tile.model';

@Component({
  selector: 'tracking-lane',
  standalone: true,
  imports: [ CommonModule, FormsModule, TrackingLaneWheelComponent, TrackingLaneLineComponent, TrackingLaneConfigComponent,
    NzButtonModule, NzCheckboxModule, NzSelectModule, NzSpinModule ],
  templateUrl: './tracking-lane.component.html',
  styleUrl: './tracking-lane.component.scss'
})
export class TrackingLaneComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private trackingLaneService = inject(TrackingLaneService)
  private variablesService = inject(VariablesService);

  isCreating = false;
  isLoading = false;
  trackingLane: TrackingLane = { name: '', nodes: [], data: { variable_cancelled: '', variable_tracking_end_variable: '' } };
  currentNodeId: string | null = null;
  resolvedNodes: ResolvedTrackingLaneNode[] = [];
  columnDefinitions: IColumnDefinition[] = [];
  simulateCancelled = false;
  previewLoaded = false;
  isSaving = false;

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    this.isCreating = !id;

    if (id) {
      this.isLoading = true;
      this.trackingLaneService.getTrackingLane(id).subscribe({
        next: lane => {
          this.trackingLane = lane;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        }
      });
    } else {
      this.trackingLane = { name: '', nodes: [], data: { variable_cancelled: '', variable_tracking_end_variable: '' } };
    }

    this.variablesService.getColumnDefinitions().subscribe((columns: IColumnDefinition[]) => {
      this.columnDefinitions = (columns ?? []).slice().sort((a, b) =>
        (a.headerName ?? a.field ?? '').localeCompare(b.headerName ?? b.field ?? '')
      );
    });
  }

  filterVariableOption: NzFilterOptionType = (input, option) => {
    const term = input.toLowerCase();
    const label = (option.nzLabel ?? '').toString().toLowerCase();
    const value = (option.nzValue ?? '').toString().toLowerCase();
    return label.includes(term) || value.includes(term);
  };

  onNodesChange(nodes: TrackingLaneNode[]): void {
    this.trackingLane = { ...this.trackingLane, nodes };
    this.updateResolvedNodes();
  }

  onCurrentNodeIdChange(currentNodeId: string | null): void {
    this.currentNodeId = currentNodeId;
    this.updateResolvedNodes();
  }

  loadPreview(): void {
    this.previewLoaded = true;
    this.updateResolvedNodes();
  }

  private updateResolvedNodes(): void {
    this.resolvedNodes = resolveTrackingLaneNodes(this.trackingLane.nodes, this.currentNodeId);
  }

  get canSave(): boolean {
    const allNamed = (nodes: TrackingLaneNode[]): boolean =>
      nodes.every(node => node.data.name.trim().length > 0 && allNamed(node.children));

    return (
      this.trackingLane.name.trim().length > 0 &&
      this.trackingLane.nodes.length > 0 &&
      allNamed(this.trackingLane.nodes)
    );
  }

  save() {
    this.isSaving = true;
    this.trackingLaneService.updateTrackingLane(this.trackingLane).subscribe({
      next: (res) => { this.isSaving = false; },
      error: (err) => { console.log(err); this.isSaving = false; }
    })
  }
}
